import { text } from '@clack/prompts'
import as from 'ansis'
import { Command } from 'commander'
import { z } from 'zod'

import { bundle } from '@scalar/json-magic/bundle'
import { fetchUrls, readFiles } from '@scalar/json-magic/bundle/plugins/node'
import { parseJsonOrYaml } from '@scalar/oas-utils/helpers'
import { validate } from '@scalar/openapi-parser'
import { BaseCollections } from '@scalar-org/entities/database'
import { teamSchema } from '@scalar-org/entities/team'
import {
  docVersionSchema,
  namespaceSchema,
  slugSchema,
} from '@scalar-org/entities/utility'
import { slugify } from '@scalar-org/helpers/url'
import { randomSlug } from '@scalar-org/random-slug'
import { registry } from '@scalar-org/urls'

import { DEFAULT_FETCH_CONCURRENCY } from '@/domains/document/bundle'
import { getFileOrUrl } from '@/domains/document/helpers'
import { registryUrl } from '@/domains/registry/helpers/links'
import { apiDashboardUrl } from '@/domains/registry/publish/helpers/links'
import { apiService, getAuthData, getDatabaseClient } from '@/helpers'
import { output } from '@/helpers/std-out'

export const PublishCommand = () => {
  const cmd = new Command('publish')

  cmd.description('Publish an OpenAPI document to the Scalar registry')

  // ---------------------------------------------------------------------------
  // Args
  cmd.argument('[file]', 'OpenAPI file to upload')

  // ---------------------------------------------------------------------------
  // Options
  cmd.option(
    '--slug <slug>',
    'Slug identifier for the registry entry. Defaults to title.',
  )
  cmd.option('--namespace <namespace>', 'Scalar team namespace')
  cmd.option('--version <version>', 'API version (e.g. 0.1.0)')
  cmd.option('--private', 'Make API private', false)
  cmd.option('--no-current', 'Do not set as the current version')
  cmd.option('--force', 'Force override an existing version', false)

  cmd.option('--bundle', 'Bundle all external references before uploading')
  cmd.option(
    '--treeShake',
    'Remove unused components from the bundled document',
  )
  cmd.option(
    '--urlMap',
    'Generate a map of resolved URLs when bundling the document',
  )
  cmd.option(
    '--fetchLimit <limit>',
    'Maximum number of concurrent URLs to fetch when bundling the document',
  )

  cmd.action(async (file, args) => {
    const { error, data } = z
      .object({
        slug: slugSchema.optional(),
        file: z
          .string()
          .min(1, 'Please provide a valid file path or URL for your document.')
          .optional(),
        namespace: namespaceSchema.optional(),
        version: docVersionSchema.optional(),
        private: z.coerce.boolean(),
        current: z.coerce.boolean(),
        force: z.coerce.boolean(),
        bundle: z.coerce.boolean().optional(),
        fetchLimit: z.coerce.number().optional(),
        urlMap: z.coerce.boolean().default(false),
        treeShake: z.coerce.boolean().default(false),
      })
      .safeParse({
        file,
        slug: args.slug,
        namespace: args.namespace,
        version: args.version,
        private: args.private,
        current: args.current,
        force: args.force,
      })

    if (error)
      return output
        .error()
        .title('Invalid input')
        .message(error.issues[0].message)
        .exit('error')

    const auth = await getAuthData()
    auth.addEventListener((state) => Object.assign(auth, state))

    const db = await getDatabaseClient()

    const team = await db.getOne({
      collectionPath: BaseCollections.Teams,
      query: {
        uid: auth.teamUid,
      },
      schema: teamSchema,
    })

    if (team.error)
      return output
        .error()
        .title('An error occurred when fetching your team.')
        .message(team.message)
        .exit('error')

    /**
     * Most teams will have 1 namespace, so we default to that.
     * Ideally we should also have a global cli namespace selected.
     */
    const namespace = data.namespace || team.data.namespaces.at(0)

    if (!namespace) {
      return output
        .error()
        .title('Missing namespace')
        .message(
          'Could not find a valid namespace for your API. Please specify a namespace or contact support.',
        )
        .exit('error')
    }

    const client = await apiService()

    const namespaceApisRes = await client.managedDocs.getApiDocs(namespace)

    if (namespaceApisRes.error)
      return output.error().title(namespaceApisRes.message).exit('error')

    const apiFile =
      data.file ||
      (
        await text({
          message: 'Please enter a file path or URL for your document',
          placeholder: './api.yaml',
          validate(value: string) {
            if (value.length === 0) return 'File path or URL is required!'
          },
        })
      ).toString()

    const rawDocument = await getFileOrUrl(apiFile)

    const parsed = parseJsonOrYaml(rawDocument)

    if (!rawDocument) {
      return output
        .error()
        .title('Invalid file')
        .message('Please provide a valid file path for your document.')
        .exit('error')
    }

    const bundlingPlugins = [readFiles()]

    // Only resolve external references if full bundling is requested
    if (data.bundle) {
      bundlingPlugins.push(
        fetchUrls({
          limit: data.fetchLimit ?? DEFAULT_FETCH_CONCURRENCY,
        }),
      )
    }

    const bundledDocument = await bundle(parsed, {
      plugins: bundlingPlugins,
      treeShake: data.treeShake,
      urlMap: data.urlMap,
    })

    const result = await validate(bundledDocument)

    if (!result.valid || !result.specification) {
      return output
        .error()
        .title('Invalid API document')
        .message('File does not match the OpenAPI specification.')
        .table([result.errors?.map((err) => err.message) || []])
        .exit('error')
    }

    const apiSlug =
      data.slug ||
      (result.specification.info?.title
        ? slugify(result.specification.info.title)
        : randomSlug({ words: 3 }))

    const parsedVersion = docVersionSchema.safeParse(
      data.version || result.specification.info?.version,
    )

    /**
     * Spec version should be the semver source of truth,
     * but to reduce friction we allow publish with a provisional
     * semver which we ask the user for.
     */
    const version =
      parsedVersion.data ||
      (
        await text({
          message: 'What is the version of your document (e.g. 0.1.0)?',
          validate(value: string) {
            if (value.length === 0) return 'Version is required.'
            if (docVersionSchema.safeParse(value)?.error)
              return 'Version must be a non-empty string'
          },
        })
      ).toString()

    const logger = output
      .info()
      .line()
      .loader(`Publishing ${apiSlug} to Scalar registry...`)

    const matchingApi = namespaceApisRes.data.find(
      (api) => api.slug === apiSlug,
    )

    const document = JSON.stringify(bundledDocument)

    /**
     * If an existing registry API has been matched by slug,
     * we create a new version (or override if force is supplied),
     * otherwise we automatically create a new API entry.
     */
    const publishRes = matchingApi
      ? await client.managedDocs.addVersion(namespace, apiSlug, {
          document,
          version,
          force: data.force,
          isCurrent: data.current,
        })
      : await client.managedDocs.create(namespace, {
          title: result.specification.info?.title || 'My API',
          description: result.specification.info?.description,
          version,
          slug: apiSlug,
          isPrivate: data.private,
          document,
        })

    if (publishRes.error)
      return output.error().title(publishRes.message).exit('error')

    logger
      .update('loader', 0, {
        content: `Successfully published v${version} for ${apiSlug}.`,
      })
      .line()
      .print()

    const api = registry(namespace).apis(apiSlug)

    const dashboardUrl = apiDashboardUrl(
      matchingApi?.uid ?? publishRes.data.uid,
    )

    // ---------------------------------------------------------------------------
    // Quick Links

    output
      .info()
      .line(`${as.green('➜')} ${as.white.bold('View in dashboard:')}`)
      .line(as.cyan(dashboardUrl))
      .line()
      .line(`${as.green('➜')} ${as.white.bold('Registry Preview:')}`)
      .line(as.cyan(registryUrl(api.url({ version }))))
      .line()
      .line(`${as.green('➜')} ${as.white.bold('Registry YAML:')}`)
      .line(as.cyan(registryUrl(api.url({ version, format: 'yaml' }))))
      .line()
      .line(`${as.green('➜')} ${as.white.bold('Registry JSON:')}`)
      .line(as.cyan(registryUrl(api.url({ version, format: 'json' }))))
      .print()
  })

  return cmd
}
